import {useState} from "react";
import styled from "styled-components/macro";
import classNames from "classnames";
import Header from "../../components/header";
import TopBar from "../../components/top_bar";
import SortBar from "../../components/sort_bar";
import CommunityHeader from "../../components/community_header";
import Container from "../../components/container";
import Main from '../../components/main';
import Sidebar from "../../components/sidebar";
import Footer from "../../components/footer";
import {useFeedback} from "../../store/feedback";
import Wrapper from "../../components/wrapper";
import {useCommunityStyle} from "../../api/communities";
import {useBaseCommunity, useStoreComment} from "../../store/hooks";
import HeaderData from "../../components/header_data";
import NavLink from "../../components/nav_link";
import {useQuery} from "../../utils/router";
import {useMarkMessageUnread, useMessages} from "../../api/message";
import Message from "../../components/message";
import Comment from "../../components/comment";
import MessageSkeleton from "../../components/message_skeleton";
import useAtBottom from "../../utils/use_at_bottom";
import {useModal} from "../../store";
import CreateMessage from "../../components/create_message";
import {isPseudoCommunity} from "../../store/communites";
import {themecolors} from "../../utils/themecolors";
import useWindowDimensions from "../../utils/dimensions";
import LegacySortBar from "../../components/legacy/legacy_sort_bar";

const Items = styled.div``;
const Item = styled.div``;
const Manage = styled.div``;
const New = styled.div``;

const MessageItem = styled(({className, message, canMarkUnread, canMarkRead, markMessageUnread, type}) => {
    className = classNames(className, message?.is_unread && type !== 'unread' && type !== 'sent' ? 'unread' : 'read');
    const comment = useStoreComment(message?.comment_id);
    const callbacks = {
        markRead: canMarkRead && message?.is_unread && (() => markMessageUnread(message.id, false)),
        markUnread: canMarkUnread && !message?.is_unread && (() => markMessageUnread(message.id, true)),
    };
    const content =
        message ? (
            comment ?
                <Comment
                    comment={comment}
                    contextual
                    showCommunity
                    hideComments
                    {...callbacks}
                /> :
                <Message
                    showSubject
                    message={message}
                    {...callbacks}
                />
        ) : <MessageSkeleton/>;
    if (callbacks.markRead) {
        return <div className={className} onClick={callbacks.markRead}>{content}</div>;
        // return <DivButton className={className} onClick={callbacks.markRead}>{content}</DivButton>;
    }
    return <div className={className}>{content}</div>;
})`
  ${Message}, ${Comment}, ${MessageSkeleton} {
    border-bottom: ${({theme}) => theme.color.name === "dark" ? `1px solid ${themecolors.dark_background_5}` : theme.standard_border};
  }
`;

const Messages = styled(({className}) => {

    const community = useBaseCommunity();
    const style = useCommunityStyle(community);

    const {type = "all"} = useQuery();
    const loading = useFeedback();
    const [messages, setMessages] = useState(null);
    const { loadNextPage } = useMessages(loading.channel, setMessages, type || 'all', 1);
    const detector = useAtBottom(loadNextPage);
    const markMessageUnread = useMarkMessageUnread(null, setMessages);

    const modal = useModal();
    const handleMessage = () => {
        modal.open(CreateMessage);
    };

    const {width} = useWindowDimensions();

    return (
        <div className={className}>
            <HeaderData title='Messages' community={community} community_style={style}/>
            <Header>
                {(community !== "TheDonald" || width <= 780) && <TopBar legacyConversion={community === "TheDonald"} page={'Messages'}/>}
                {!isPseudoCommunity(community) &&
                    <>
                        <CommunityHeader community={community}/>
                    </>
                }
                {community === "TheDonald" && width > 780 && <LegacySortBar />}
            </Header>
            <SortBar community={community}/>
            <Container>
                <Wrapper>
                    <Manage>
                        <Items>
                            <Item className={(!type || type === 'all') && 'active'}>
                                <NavLink href={`/messages?type=all`} react_route>All</NavLink>
                            </Item>
                            <Item className={type === 'unread' && 'active'}>
                                <NavLink href={`/messages?type=unread`} react_route>Unread</NavLink>
                            </Item>
                            <Item className={type === 'messages' && 'active'}>
                                <NavLink href='/messages?type=messages' react_route>Messages</NavLink>
                            </Item>
                            <Item className={type === 'comments' && 'active'}>
                                <NavLink href='/messages?type=comments' react_route>Comments</NavLink>
                            </Item>
                            <Item className={type === 'sent' && 'active'}>
                                <NavLink href='/messages?type=sent' react_route>Sent</NavLink>
                            </Item>
                            <Item onClick={handleMessage}>
                                Create Message
                            </Item>
                        </Items>
                    </Manage>
                    <Main>
                        <div>
                            {messages?.map((message) => (
                                <MessageItem
                                    key={message.id}
                                    message={message}
                                    markMessageUnread={markMessageUnread}
                                    canMarkRead={type !== 'unread' && type !== 'sent'}
                                    canMarkUnread={type !== 'sent'}
                                    type={type}
                                />
                            ))}
                            {detector}
                        </div>
                    </Main>
                </Wrapper>
                <Sidebar sidebar={style && style.sidebar} community={community} />
            </Container>
            <Footer/>
        </div>
    );
})`
  ${Main} {
    font-size: 14px;
  
  }

  ${Item} {
    padding: 6px 16px;
    margin-right: 6px;
    border-radius: 48px;
    white-space: nowrap;
    cursor: pointer;
  }
  ${Items} {
    display: flex;
    align-items: center;
  }

  ${Manage} {
    display: flex;
    font-size: 15px;
    margin-bottom: 12px;
  }

  ${Item} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${Item}.active {
    background-color: ${({theme}) => theme.color.name === "dark" ? themecolors.dark_background_2 : '#fff'};
  }
  
  ${New} {
    cursor: pointer;
    margin-left: 6px;
    margin-right: 32px;
  }
  
  ${MessageItem}.unread {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.dark_field : theme.placeholder_white_2};
  }

  @media only screen and (max-width: 780px) {
    ${Manage} {
      display: initial;
    }
    ${Items} {
      padding: 8px;
      overflow-y: scroll;
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }
    ${Items}::-webkit-scrollbar {
      display: none;
    }
    ${New} {
      margin-left: 0;
      margin-right: 12px;
    }
  }
  
  }
`;

export default Messages;
